/**
 * A React-componentent that represents a button that when pressed, exports the
 * given Piano data entry fields to a PDF-document that can be downloaded.
 * Generating the PDF-document is performed in the client using the
 * {@link EntryExporter} utility.
 *
 * To use this component you need to pass two properties:
 *
 * - title
 * - fields
 *
 * Where title is a string that will be used as the title in the PDF-document
 * and fields is either an array or object containing data entry fields to be
 * exported.
 *
 * There are 2 optional properties that can be passed that control the look
 * and feel of the button:
 *
 * - color
 * - label
 *
 * Where color is a string that will be used as CSS-property to style the
 * button (defaults to '#444') and label controls the label of the actual
 * button (defaults to 'Export to PDF').
 *
 * NOTE: THIS MODULE HAS EXTERNAL DEPENDENCIES NOT INSTALLED BY NPM
 * This component uses the service https://www.emailjs.com/ that provides a
 * HTTP API that acts as a SMTP proxy for an email server.
 * Emailjs provides a javascript library for easy use. However this provided
 * library is closed-source and not published on npm. The library cannot be used
 * in webpack using script-loader either, so the file needs to be loaded in a
 * script tag in the HTML-document.
 *
 * In this project the library is installed by bower
 * (see dependencies in client/bower.json) and then included in
 * client/app/index.html as a script-tag.
 *
 */

import React, { FC, useCallback, useEffect, useState } from "react";
import _ from "lodash";

import config from "../../../config";
import F7 from "../../../utils/F7";
import { EntryExporter, Formats } from "../../../utils/export/entry";
import { EntryFormatter } from "../../../utils/format/entry";
import { FileDownloader } from "../../../utils/files/download";
import emailjs from "@emailjs/browser";
import { usePrevious } from "../../../hooks/usePrevious";
import { useCurrentDatabaseManager } from "../../../hooks/database/useDatabases";
import { useCurrentInventoryId } from "../../../hooks/url";

/**==============================================================================
 * Constants
 */

/**
 * Enum with the different states the main component can have.
 *
 * @typedef STATES
 * @type object
 * @property {Symbol} START - Starting state, waiting for user input
 * @property {Symbol} EXPORT_BUSY - Busy exporting PDF
 * @property {Symbol} EXPORT_FAIL - Failed exporting PDF
 * @property {Symbol} EXPORT_DONE - Done exporting PDF
 * @property {Symbol} DOWNLOAD_BUSY - Busy downloading PDF
 * @property {Symbol} DOWNLOAD_FAIL - Failed downloading PDF
 * @property {Symbol} DOWNLOAD_DONE - Done downloading PDF
 * @property {Symbol} SEND_BUSY - Busy sending PDF email
 * @property {Symbol} SEND_FAIL - Failed sending PDF email
 * @property {Symbol} SEND_DONE - Done sending PDF email
 */
enum States {
  START = "START",
  EXPORT_BUSY = "EXPORT_BUSY",
  EXPORT_FAIL = "EXPORT_FAIL",
  EXPORT_DONE = "EXPORT_DONE",
  DOWNLOAD_BUSY = "DOWNLOAD_BUSY",
  DOWNLOAD_FAIL = "DOWNLOAD_FAIL",
  DOWNLOAD_DONE = "DOWNLOAD_DONE",
  SEND_BUSY = "SEND_BUSY",
  SEND_FAIL = "SEND_FAIL",
  SEND_DONE = "SEND_DONE",
}

/**==============================================================================
 * Components
 */

/**
 * Component for showing the main components current status from its state.
 * This component does not keep it's own state, nor does it manipulate the main
 * components state. It merely checks the state and renders a status message
 * box accordingly.
 */
const StatusMessage: FC<{ currState: States; isBusy: boolean }> = (props) => {
  const statusText = (() => {
    switch (props.currState) {
      case States.DOWNLOAD_DONE:
        if (!config.isApp) {
          return "Created PDF-document opened in new tab!";
        } else {
          return "Created PDF-document saved in documents folder!";
        }
      case States.SEND_DONE:
        return "Created PDF-document sent to given email!";
      case States.EXPORT_FAIL:
        return "Could not generate PDF-document export! Internal error encountered";
      case States.DOWNLOAD_FAIL:
        return "Could not download generated PDF-document! Internal error encountered";
      case States.SEND_FAIL:
        return "Could not send generated PDF-document! Internal error encountered";
      default:
        break;
    }

    return "";
  })();

  const statusType = (() => {
    switch (props.currState) {
      case States.EXPORT_FAIL:
      case States.DOWNLOAD_FAIL:
      case States.SEND_FAIL:
        return "alert-danger";
      case States.EXPORT_DONE:
      case States.DOWNLOAD_DONE:
      case States.SEND_DONE:
        return "alert-success";
      default:
        break;
    }

    return "alert-info";
  })();

  const hideStatus = props.isBusy || props.currState === States.START || props.currState === States.EXPORT_DONE;

  if (hideStatus) {
    return null;
  }

  return (
    <div
      className={"status-message alert " + statusType}
      style={{
        width: "90%",
        marginLeft: "5%",
        marginRight: "5%",
      }}
    >
      {statusText}
    </div>
  );
};

/**
 * Main component of this module. This component is implemented as a simple
 * 10-state FSM, the available states exists in the constant {@link States}.
 * State is handled inside the component by Reacts built-in state handling
 * (setState, et al).
 *
 * Note: this component updates its state when given props changes.
 */
const ExportButton: FC<{ title: string; fields: any; label?: string; color?: string }> = (props) => {
  const [currState, setCurrState] = useState<States>(States.START);
  const [documentStream, setDocumentStream] = useState(null);

  /**
   * Makes sure the components state is reset if the given props have been
   * changed, so that the new PDF-document will be generated with the new
   * properties.
   */
  const previousState = usePrevious({ title: props.title, fields: props.fields });
  useEffect(() => {
    if (!_.isEqual(previousState, { title: props.title, fields: props.fields })) {
      setCurrState(States.START);
      setDocumentStream(null);
    }
  }, [previousState, props.fields, props.title]);

  /**
   * Checks if the component is busy or can transition into another state.
   *
   * @returns {boolean}
   */
  const isBusy = currState === States.EXPORT_BUSY || currState === States.DOWNLOAD_BUSY || currState === States.SEND_BUSY;

  /**
   * Checks if the document is ready to download or if it should be created.
   *
   * @returns {boolean}
   */
  const isFileGenerated =
    documentStream !== null && (currState === States.EXPORT_DONE || currState === States.DOWNLOAD_DONE || currState === States.SEND_DONE);
  /**
   * Sends given blob as an attachment using the EmailJS service.
   *
   * @param {string} email - Email to send PDF-document to.
   * @param {BlobStream} stream - Document stream
   */
  const emailResult = useCallback((email: string, stream: any) => {
    setCurrState(States.SEND_BUSY);

    F7.showIndicator();

    FileDownloader.blobStreamAsDataURI(stream, "application/pdf")
      .then((dataURI) => {
        // emailjs requires that you remove the prefix and only pass the
        // base64 payload.
        const content = dataURI.split(",")[1];

        emailjs.init(config.emailjs.userID);

        return emailjs.send(config.emailjs.service, config.emailjs.template, {
          send_to: email,
          content,
        });
      })
      .then(() => {
        setCurrState(States.SEND_DONE);

        F7.hideIndicator();
      })
      .catch((err) => {
        setCurrState(States.SEND_FAIL);

        console.error(err);

        F7.hideIndicator();
      });
  }, []);

  /**
   * Takes care of downloading generated PDF-document to the device.
   *
   * @param stream - Document stream
   */
  const downloadResult = useCallback(
    (stream: any) => {
      setCurrState(States.DOWNLOAD_BUSY);

      const filename = EntryFormatter.generateFilename("piano-export", props.title, props.fields, ".pdf");

      try {
        FileDownloader.downloadBlobStream(filename, stream, "application/pdf");
        setCurrState(States.DOWNLOAD_DONE);
      } catch (e) {
        console.log(e);
        setCurrState(States.DOWNLOAD_FAIL);
      }
    },
    [props.fields, props.title]
  );

  /**
   * Takes care of downloading or emailing the generated PDF-document. On iOS
   * the generated PDF can't be saved locally since apps are sandboxed from
   * each other. To solve this the customer was given 4 different solutions.
   * The customer wanted to proceed with the solution of emailing the
   * PDF-document.
   *
   * @param {BlobStream} stream - Document stream
   */
  const handleResult = useCallback(
    (stream: any) => {
      if (config.isApp) {
        F7.prompt("Enter email to send export to", "Email export", (email: string) => emailResult(email, stream));

        return;
      }

      downloadResult(stream);
    },
    [downloadResult, emailResult]
  );

  const mngr = useCurrentDatabaseManager();
  const { inventoryId } = useCurrentInventoryId();

  /**
   * Handler for clicking the main button that generates a PDF-document from
   * the fields given to the component. An URL to the generated document is
   * saved in the components state. A new document will only be generated if
   * it's the first time or if the given fields have changed.
   */
  const handleClick = useCallback(() => {
    if (isBusy || !mngr || !inventoryId) {
      return;
    } else if (isFileGenerated) {
      handleResult(documentStream);
      return;
    }

    F7.showIndicator();

    setCurrState(States.EXPORT_BUSY);
    setDocumentStream(null);

    EntryExporter.exportSingle(props.title, props.fields, Formats.PDF, inventoryId, mngr)
      .then((stream) => {
        F7.hideIndicator();

        setCurrState(States.EXPORT_DONE);
        setDocumentStream(stream as any);

        handleResult(stream);
      })
      .catch((err) => {
        F7.hideIndicator();

        console.error(err);

        setCurrState(States.EXPORT_FAIL);
        setDocumentStream(null);
      });
  }, [documentStream, handleResult, inventoryId, isBusy, isFileGenerated, mngr, props.fields, props.title]);

  return (
    <div className="ExportButton">
      <div className="col-100">
        <button
          onClick={isBusy ? undefined : handleClick}
          className="button button-fill"
          disabled={isBusy}
          style={{
            width: "90%",
            marginLeft: "5%",
            marginRight: "5%",
            marginBottom: 10,
            height: 35,
            backgroundColor: props.color || "#444",
          }}
        >
          {props.label || "Export to PDF"}
        </button>
      </div>
      <div className="col-100">
        <StatusMessage isBusy={isBusy} currState={currState} />
      </div>
    </div>
  );
};

export default ExportButton;
